import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Button,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Alert
} from "@mui/material";
import { ModalBox } from "../../../styles/ModalStyles";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  AccountTree as AccountTreeIcon,
  Description as DocumentIcon
} from "@mui/icons-material";

const AgregarRelacionClienteOrganismoModal = ({ open, onClose, onSubmit, cliente, organismosConTipos }) => {
  const [organismosSeleccionados, setOrganismosSeleccionados] = useState([]);
  const [tiposSeleccionados, setTiposSeleccionados] = useState([]);
  const [expandedOrganismos, setExpandedOrganismos] = useState({});
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (open && cliente) {
      // Limpiar errores al abrir el modal
      setValidationError("");
      
      // Inicializar desde las relaciones detalladas
      if (cliente.relacionesOrganismoTipo && cliente.relacionesOrganismoTipo.length > 0) {
        const organismosIds = cliente.relacionesOrganismoTipo.map(r => r.idOrganismoEmisor);
        const tiposIds = cliente.relacionesOrganismoTipo.flatMap(r => r.idTipoDocumento);
        
        setOrganismosSeleccionados(organismosIds);
        setTiposSeleccionados(tiposIds);
        
        // Expandir acordeones de organismos seleccionados
        const expanded = {};
        organismosIds.forEach(id => {
          expanded[id] = true;
        });
        setExpandedOrganismos(expanded);
      } else {
        setOrganismosSeleccionados([]);
        setTiposSeleccionados([]);
        setExpandedOrganismos({});
      }
    }
  }, [open, cliente]);

  const handleOrganismoChange = (organismoId, checked) => {
    // Limpiar error cuando se hace un cambio
    setValidationError("");
    
    setOrganismosSeleccionados(prev => {
      const newSelection = checked 
        ? [...prev, organismoId]
        : prev.filter(id => id !== organismoId);
      
      // Si se deselecciona un organismo, también deseleccionar sus tipos
      if (!checked) {
        const organismo = organismosConTipos.find(o => o.id === organismoId);
        if (organismo) {
          const tiposDelOrganismo = organismo.idTipoDocumento || [];
          setTiposSeleccionados(prevTipos => 
            prevTipos.filter(tipoId => !tiposDelOrganismo.includes(tipoId))
          );
        }
      }
      
      return newSelection;
    });

    // Expandir/contraer el acordeón
    setExpandedOrganismos(prev => ({
      ...prev,
      [organismoId]: checked
    }));
  };

  const handleTipoChange = (tipoId, checked) => {
    // Limpiar error cuando se hace un cambio
    setValidationError("");
    
    setTiposSeleccionados(prev => 
      checked 
        ? [...prev, tipoId]
        : prev.filter(id => id !== tipoId)
    );
  };

  const handleAccordionChange = (organismoId) => (event, isExpanded) => {
    setExpandedOrganismos(prev => ({
      ...prev,
      [organismoId]: isExpanded
    }));
  };

  // Función para validar que cada organismo seleccionado tenga al menos un tipo
  const validateSelection = () => {
    const organismosInvalidos = [];
    
    organismosSeleccionados.forEach(organismoId => {
      const organismo = organismosConTipos.find(o => o.id === organismoId);
      if (organismo) {
        const tiposDelOrganismo = organismo.idTipoDocumento || [];
        const tiposSeleccionadosDeEsteOrganismo = tiposDelOrganismo.filter(tipoId => 
          tiposSeleccionados.includes(tipoId)
        );
        
        if (tiposSeleccionadosDeEsteOrganismo.length === 0) {
          organismosInvalidos.push(organismo.nombre);
        }
      }
    });
    
    return organismosInvalidos;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    
    // Validar antes de enviar
    const organismosInvalidos = validateSelection();
    
    if (organismosInvalidos.length > 0) {
      const errorMessage = organismosInvalidos.length === 1 
        ? `El organismo "${organismosInvalidos[0]}" debe tener al menos un tipo de documento seleccionado.`
        : `Los siguientes organismos deben tener al menos un tipo de documento seleccionado: ${organismosInvalidos.join(', ')}.`;
      
      setValidationError(errorMessage);
      return;
    }
    
    // Construir la estructura de datos para la relación triple
    const organismosConTiposSeleccionados = organismosSeleccionados.map(organismoId => {
      const organismo = organismosConTipos.find(o => o.id === organismoId);
      const tiposDeEsteOrganismo = organismo?.idTipoDocumento?.filter(tipoId => 
        tiposSeleccionados.includes(tipoId)
      ) || [];
      
      return {
        idOrganismoEmisor: organismoId,
        idTipoDocumento: tiposDeEsteOrganismo
      };
    }).filter(o => o.idTipoDocumento.length > 0); // Solo organismos con tipos seleccionados

    onSubmit({
      idCliente: cliente.id,
      organismosTipos: organismosConTiposSeleccionados
    });
  };

  const isOrganismoSeleccionado = (organismoId) => {
    return organismosSeleccionados.includes(organismoId);
  };

  const isTipoSeleccionado = (tipoId) => {
    return tiposSeleccionados.includes(tipoId);
  };

  const getTiposSeleccionadosParaOrganismo = (organismoId) => {
    const organismo = organismosConTipos.find(o => o.id === organismoId);
    if (!organismo) return 0;
    
    const tiposDelOrganismo = organismo.idTipoDocumento || [];
    return tiposDelOrganismo.filter(tipoId => tiposSeleccionados.includes(tipoId)).length;
  };

  // Función para determinar si un organismo tiene error (seleccionado pero sin tipos)
  const hasOrganismoError = (organismoId) => {
    if (!isOrganismoSeleccionado(organismoId)) return false;
    return getTiposSeleccionadosParaOrganismo(organismoId) === 0;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalBox sx={{ width: '90%', maxWidth: 1000, maxHeight: '90vh', overflow: 'auto' }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant='h6' sx={{ mb: 3 }}>
          Gestionar Organismos y Tipos de Documento
        </Typography>
        
        <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 1 }}>
          Cliente: {cliente?.nombre}
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 3 }}>
          Seleccione los organismos emisores y sus tipos de documento asociados para este cliente.
        </Typography>

        {/* Mostrar error de validación */}
        {validationError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {validationError}
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          {/* Organismos y Tipos de Documento */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountTreeIcon />
                Organismos Emisores y Tipos de Documento
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                {organismosConTipos.map((organismo) => (
                  <Accordion
                    key={organismo.id}
                    expanded={expandedOrganismos[organismo.id] || false}
                    onChange={handleAccordionChange(organismo.id)}
                    sx={{ 
                      mb: 1,
                      // Agregar borde rojo si hay error
                      ...(hasOrganismoError(organismo.id) && {
                        border: '2px solid',
                        borderColor: 'error.main',
                        backgroundColor: 'error.50'
                      })
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: isOrganismoSeleccionado(organismo.id) 
                          ? (hasOrganismoError(organismo.id) ? 'error.50' : 'primary.50')
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isOrganismoSeleccionado(organismo.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleOrganismoChange(organismo.id, e.target.checked);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1">
                                {organismo.nombre}
                              </Typography>
                              {organismo.sigla && (
                                <Chip 
                                  label={organismo.sigla} 
                                  size="small" 
                                  variant="outlined" 
                                />
                              )}
                            </Box>
                          }
                          sx={{ margin: 0 }}
                        />
                        {isOrganismoSeleccionado(organismo.id) && (
                          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                            {hasOrganismoError(organismo.id) ? (
                              <Chip
                                label="¡Debe seleccionar tipos!"
                                size="small"
                                color="error"
                                variant="filled"
                              />
                            ) : (
                              <Chip
                                label={`${getTiposSeleccionadosParaOrganismo(organismo.id)} tipos seleccionados`}
                                size="small"
                                color="secondary"
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    </AccordionSummary>
                    
                    <AccordionDetails>
                      <Box sx={{ pl: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Tipos de documento disponibles para {organismo.nombre}:
                        </Typography>
                        
                        <FormGroup>
                          {organismo.tipoDocumento && organismo.tipoDocumento.length > 0 ? (
                            organismo.idTipoDocumento.map((tipoId, index) => (
                              <FormControlLabel
                                key={tipoId}
                                control={
                                  <Checkbox
                                    checked={isTipoSeleccionado(tipoId)}
                                    onChange={(e) => handleTipoChange(tipoId, e.target.checked)}
                                    disabled={!isOrganismoSeleccionado(organismo.id)}
                                  />
                                }
                                label={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DocumentIcon fontSize="small" />
                                    {organismo.tipoDocumento[index]}
                                  </Box>
                                }
                              />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No hay tipos de documento asociados a este organismo.
                            </Typography>
                          )}
                        </FormGroup>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
            >
              Guardar cambios
            </Button>
            <Button onClick={onClose} variant="contained" color="error">
              Cancelar
            </Button>
          </Box>
        </form>
      </ModalBox>
    </Modal>
  );
};

export default AgregarRelacionClienteOrganismoModal;